import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ClientAvatar } from "@/components/ClientAvatar";
import MonthNav from "@/components/MonthNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, FileText, Pill, AlertCircle, CheckCircle2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

function pad(n) { return String(n).padStart(2, "0"); }

export default function AdminClientCalendar() {
  const { clientId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(parseInt(params.get("y")) || now.getFullYear());
  const [month, setMonth] = useState(parseInt(params.get("m")) || now.getMonth() + 1);

  const [client, setClient] = useState(null);
  const [cal, setCal] = useState(null);
  const [notes, setNotes] = useState([]);
  const [emar, setEmar] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingNote, setEditingNote] = useState(null); // {id, summary_note, attendance, overnight_stayed}
  const [savingEdit, setSavingEdit] = useState(false);

  const reloadDay = async () => {
    const start = `${year}-${pad(month)}-01`;
    const end = `${year}-${pad(month)}-${pad(cal?.total_days || 31)}`;
    const [n, e, c] = await Promise.all([
      api.get(`/daily-notes?client_id=${clientId}&start_date=${start}&end_date=${end}`),
      api.get(`/emar-logs?client_id=${clientId}&start_date=${start}&end_date=${end}`),
      api.get(`/calendar-status?client_id=${clientId}&year=${year}&month=${month}`),
    ]);
    setNotes(n.data); setEmar(e.data); setCal(c.data);
  };

  const saveEdit = async () => {
    if (!editingNote) return;
    setSavingEdit(true);
    try {
      await api.put(`/daily-notes/${editingNote.id}`, {
        summary_note: editingNote.summary_note,
        attendance: editingNote.attendance,
        overnight_stayed: editingNote.overnight_stayed,
      });
      toast.success("Daily note updated");
      setEditingNote(null);
      await reloadDay();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingEdit(false); }
  };


  useEffect(() => {
    api.get(`/clients/${clientId}`).then(({ data }) => setClient(data));
  }, [clientId]);

  useEffect(() => {
    api.get(`/calendar-status?client_id=${clientId}&year=${year}&month=${month}`).then(({ data }) => setCal(data));
    const start = `${year}-${pad(month)}-01`;
    const end = `${year}-${pad(month)}-${pad(cal?.total_days || 31)}`;
    api.get(`/daily-notes?client_id=${clientId}&start_date=${start}&end_date=${end}`).then(({ data }) => setNotes(data));
    api.get(`/emar-logs?client_id=${clientId}&start_date=${start}&end_date=${end}`).then(({ data }) => setEmar(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year, month]);

  if (!cal || !client) return <p className="text-muted-foreground">Loading…</p>;

  const noteSet = new Set(cal.note_dates);
  const greenSet = new Set(cal.green_dates || []);
  const blackSet = new Set(cal.black_dates || []);
  const emarSet = new Set(cal.emar_dates);
  const hasMeds = cal.has_medications;
  const today = new Date();
  const isCurrent = (d) => year === today.getFullYear() && month === today.getMonth() + 1 && d === today.getDate();

  const firstDow = new Date(year, month - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= cal.total_days; d++) cells.push(d);

  const note = (d) => notes.find((n) => n.date === `${year}-${pad(month)}-${pad(d)}`);
  const emarOnDay = (d) => emar.find((e) => e.date === `${year}-${pad(month)}-${pad(d)}`);

  const selectedNote = selectedDate ? note(selectedDate) : null;
  const selectedEmar = selectedDate ? emarOnDay(selectedDate) : null;

  return (
    <div className="space-y-6" data-testid="admin-client-calendar">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/notes")} data-testid="back-to-notes">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Daily Notes
        </Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ClientAvatar
            name={client.name}
            photoUrl={client.photo_data_url}
            className="h-14 w-14 rounded-md object-cover"
            placeholderClassName="h-14 w-14 rounded-md bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-xl"
            testId="calendar-client-avatar"
          />
          <div>
            <p className="text-label">Daily Notes Dashboard</p>
            <h1 className="font-display text-3xl font-bold tracking-tight">{client.name}</h1>
            <p className="text-sm text-muted-foreground">{hasMeds ? "eMAR + Daily Note required each day" : "Daily Note required each day"}</p>
          </div>
        </div>
        <MonthNav year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} testId="calendar-month-nav" />
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <p key={d} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-center py-1">{d}</p>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {cells.map((d, i) => {
            if (d == null) return <div key={`empty-${i}`} />;
            const dateStr = `${year}-${pad(month)}-${pad(d)}`;
            const hasNote = noteSet.has(dateStr);
            const hasEmar = emarSet.has(dateStr);
            const isGreen = greenSet.has(dateStr);
            const isBlack = blackSet.has(dateStr);
            const fullyComplete = hasNote && (!hasMeds || hasEmar);
            const today = new Date();
            const dateObj = new Date(year, month - 1, d);
            const isFuture = dateObj > today;
            let tone;
            if (fullyComplete && isGreen) tone = "border-emerald-500 bg-emerald-50";
            else if (fullyComplete && isBlack) tone = "border-slate-900 bg-slate-900 text-white";
            else if (hasNote || hasEmar) tone = "border-amber-400 bg-amber-50";
            else if (isFuture) tone = "border-border bg-white";
            else tone = "border-red-500 bg-red-50";
            return (
              <button
                key={dateStr}
                onClick={() => setSelectedDate(d)}
                className={`aspect-square rounded-md border-2 p-1.5 text-left transition-colors flex flex-col justify-between hover:bg-secondary ${tone} ${isCurrent(d) ? "ring-2 ring-primary" : ""}`}
                data-testid={`cal-day-${d}`}
              >
                <p className={`font-semibold text-sm ${isBlack && fullyComplete ? "text-white" : ""}`}>{d}</p>
                <div className="flex items-center gap-1">
                  <span
                    title="Daily Note"
                    className={`h-2 w-2 rounded-full ${hasNote ? (isBlack && fullyComplete ? "bg-white" : "bg-primary") : "bg-border"}`}
                    data-testid={`cal-note-dot-${d}`}
                  />
                  {hasMeds && (
                    <span
                      title="eMAR"
                      className={`h-2 w-2 rounded-full ${hasEmar ? "bg-emerald-600" : "bg-red-400"}`}
                      data-testid={`cal-emar-dot-${d}`}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-emerald-500 bg-emerald-50" /> Note + Stayed (billable)</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-slate-900 bg-slate-900" /> Note + Did not stay</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-red-500 bg-red-50" /> Missing / past due</span>
          {hasMeds && <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-600" /> eMAR submitted</span>}
        </div>
      </Card>

      <Dialog open={!!selectedDate} onOpenChange={(o) => !o && setSelectedDate(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {client.name} — {year}-{pad(month)}-{pad(selectedDate)}
            </DialogTitle>
            <DialogDescription>Submission detail for the selected day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm">
            <section>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <FileText className="w-4 h-4 text-primary" />
                <h3 className="font-display font-semibold">Daily Note</h3>
                {selectedNote ? <Badge>Submitted</Badge> : <Badge variant="secondary">Missing</Badge>}
                {selectedNote && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setEditingNote({
                      id: selectedNote.id,
                      summary_note: selectedNote.summary_note || "",
                      attendance: selectedNote.attendance,
                      overnight_stayed: !!selectedNote.overnight_stayed,
                    })}
                    className="ml-auto"
                    data-testid="edit-note-btn"
                  ><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                )}
              </div>
              {selectedNote ? (
                <div className="space-y-2">
                  <p><span className="text-label mr-2">Provider</span>{selectedNote.provider_name}</p>
                  <p><span className="text-label mr-2">Attendance</span><span className="capitalize">{selectedNote.attendance}</span></p>
                  <p><span className="text-label mr-2">Overnight</span>{selectedNote.overnight_stayed ? "Yes" : "No"}</p>
                  {selectedNote.summary_note && <p className="whitespace-pre-wrap bg-secondary/40 p-2 rounded-md">{selectedNote.summary_note}</p>}
                  {selectedNote.last_edited_by && (
                    <p className="text-xs text-muted-foreground italic">
                      Last edited by {selectedNote.last_edited_by.name} ({selectedNote.last_edited_by.role})
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-red-500" /> No daily note submitted.</p>
              )}
            </section>

            {hasMeds && (
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Pill className="w-4 h-4 text-primary" />
                  <h3 className="font-display font-semibold">eMAR</h3>
                  {selectedEmar ? <Badge>Submitted</Badge> : <Badge variant="secondary">Missing</Badge>}
                </div>
                {selectedEmar ? (
                  <div className="space-y-1.5">
                    {(selectedEmar.entries || []).map((e, i) => (
                      <div key={e.medication_id || `${e.name}-${i}`} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center border border-border rounded-md p-2">
                        <p className="font-medium">{e.name} <span className="text-muted-foreground text-xs">{e.dosage}</span></p>
                        <span className="text-xs">AM: <b className={e.am_status === "missed" ? "text-red-600" : ""}>{e.am_status}</b></span>
                        <span className="text-xs">PM: <b className={e.pm_status === "missed" ? "text-red-600" : ""}>{e.pm_status}</b></span>
                      </div>
                    ))}
                    {(selectedEmar.prn_entries || []).length > 0 && (
                      <div className="pt-2 mt-2 border-t border-border">
                        <p className="text-label mb-1">PRN administered</p>
                        {selectedEmar.prn_entries.map((p, i) => (
                          <p key={`${p.medication_id || p.name}-${p.time || i}`} className="text-xs">• {p.name} {p.dosage} {p.time ? `@ ${p.time}` : ""} — {p.reason}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-red-500" /> No eMAR log submitted.</p>
                )}
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingNote} onOpenChange={(o) => !o && setEditingNote(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Daily Note</DialogTitle>
            <DialogDescription>Admin override of a past submission. This action is logged.</DialogDescription>
          </DialogHeader>
          {editingNote && (
            <div className="space-y-3">
              <div>
                <Label className="text-label">Attendance</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["present", "absent"].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setEditingNote({ ...editingNote, attendance: a })}
                      className={`p-2 rounded-md border-2 capitalize text-sm ${editingNote.attendance === a ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}
                      data-testid={`edit-attendance-${a}`}
                    >{a}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer p-2 border border-border rounded-md">
                <input
                  type="checkbox"
                  checked={editingNote.overnight_stayed}
                  onChange={(e) => setEditingNote({ ...editingNote, overnight_stayed: e.target.checked })}
                  className="h-5 w-5"
                  data-testid="edit-overnight"
                />
                <span className="text-sm">Stayed overnight</span>
              </label>
              <div>
                <Label className="text-label">Provider Intervention & Client Response Note</Label>
                <Textarea
                  rows={5}
                  value={editingNote.summary_note}
                  onChange={(e) => setEditingNote({ ...editingNote, summary_note: e.target.value })}
                  className="mt-1"
                  data-testid="edit-summary"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNote(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="save-edit">
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
